import type { CrudFormProps } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:graphql/types'

import React, { useState } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { useFetchAllProductReviews } from '@@partner:graphql/query/product/productReviews/allProductReviews.gql'
import { useUpdateProductReview } from '@@partner:graphql/mutation/catalog/updateProductReview'

import { Button, Grid, Panel, Row, Col, Rate, Avatar, Tag } from 'rsuite'
import CheckIcon from '@rsuite/icons/Check'
import CloseIcon from '@rsuite/icons/Close'
import ArrowRightIcon from '@rsuite/icons/ArrowRight'
import HistoryIcon from '@rsuite/icons/History'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import ProductReviewsGrid from '../ProductReviewGrid'
import ModerationCompleteInfo from './ModerationCompleteInfo'

import { SORT_ORDER_ASC } from '@@partner:config/graphql'
import {
  ROUTE_CATALOG_PRODUCT_EDIT,
  ROUTE_CUSTOMERS_VIEW_CUSTOMER
} from '@@partner:config/routes'
import {
  PRODUCT_REVIEW_STATUS_REJECTED,
  PRODUCT_REVIEW_STATUS_AWAITING_MODERATION,
  PRODUCT_REVIEW_STATUS_PASSED_MODERATION
} from '@@partner:config/productReview-status-codes'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'

export interface ProductReviewModerationFormProps extends CrudFormProps {
  brandSlug: string
}

export interface ProductReviewModerationState {
  current_id: number
  decisions: number[]
  showHistory: boolean
}

export const DEFAULT_PRODUCT_REVIEW_MODERATION_STATE: ProductReviewModerationState =
  {
    current_id: 0,
    decisions: [],
    showHistory: false
  }

export default function ProductReviewModerationForm ({
  brandSlug
}: ProductReviewModerationFormProps) {
  const [ state, setState ] = useState<ProductReviewModerationState>(
    DEFAULT_PRODUCT_REVIEW_MODERATION_STATE
  )

  const { data, loading, error } = useFetchAllProductReviews({
    filter: {
      and: [
        {
          fields: [ 'status' ],
          op: 'eq',
          value: [ PRODUCT_REVIEW_STATUS_AWAITING_MODERATION ]
        },
        {
          fields: [ 'id' ],
          op: 'gt',
          value: [ state.current_id ]
        }
      ]
    },
    sort: {
      fieldName: 'id',
      direction: SORT_ORDER_ASC
    },
    pager: {
      limit: 1
    }
  })

  const [ updateProductReview, { loading: loadingUpdateProductReview } ] =
    useUpdateProductReview()

  const currentReview = data?.productReviews?.edges[0]

  const product = currentReview?.product
  const customer = currentReview?.customer

  const productPreviewUrl =
    currentReview?.product?.preview?.url ?? NO_PHOTO_IMAGE

  const desicionsFilter = {
    fields: [ 'id' ],
    op: 'contains',
    value: state.decisions
  }

  const productPreviewModerationData = (status: number) => {
    return {
      id: currentReview?.id,
      product_id: currentReview?.product_id,
      comment: currentReview?.comment,
      status: status
    }
  }

  const handleNextReview = () => {
    setState((state) => ({
      ...state,
      current_id: Number(currentReview?.id),
      decisions: [ ...state.decisions, Number(currentReview?.id) ]
    }))
  }

  const handleShowHistory = () => {
    setState((state) => ({
      ...state,
      showHistory: !state.showHistory
    }))
  }

  const handleModerateReview = (status: number) => {
    updateProductReview({
      variables: {
        newData: productPreviewModerationData(status),
        upsert: true
      }
    }).then(() => {
      handleNextReview()
    })
  }

  if (!currentReview) {
    if (loading) {
      return <AppLoader.Inner content="Загрузка..." />
    }
    return (
      <ModerationCompleteInfo brandSlug={brandSlug} filter={desicionsFilter} />
    )
  }

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить отзыв #${currentReview?.id} для модерирования`}
        error={error}
      />
    )
  }

  return (
    <div className="ProductReviewModerationForm">
      <Panel shaded bordered className="mb-4">
        <div className="flex items-center justify-between mb-6">
          <span className="text-xl font-semibold">Отзыв #{currentReview?.id}</span>
          <Tag color="blue" size="lg">На модерации</Tag>
        </div>

        <Grid fluid>
          <Row gutter={24}>
            {/* Отзыв */}
            <Col md={16} sm={24}>
              <div className="mb-5">
                <div className="text-gray-500 text-sm mb-2">Рейтинг</div>
                <Rate value={currentReview?.rating} readOnly size="md" color="yellow" />
              </div>
              
              <div className="mb-5">
                <div className="text-gray-500 text-sm mb-2">Комментарий</div>
                <div className="p-4 bg-gray-100 rounded-lg text-base leading-relaxed whitespace-pre-wrap min-h-[80px]">
                  {currentReview?.comment || <span className="text-gray-400 italic">Комментарий отсутствует</span>}
                </div>
              </div>
            </Col>

            {/* Товар и покупатель */}
            <Col md={8} sm={24}>
              {/* Карточка товара */}
              {product && (
                <div className="mb-4">
                  <div className="text-gray-500 text-sm mb-2">Товар</div>
                  <Link
                    to={generatePath(ROUTE_CATALOG_PRODUCT_EDIT, {
                      brandSlug,
                      productId: product.id as GQL_ID
                    })}
                    target="_blank"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <img 
                      src={productPreviewUrl} 
                      alt={product?.name}
                      className="w-14 h-14 object-cover rounded border border-gray-200 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-blue-600 hover:underline line-clamp-2 text-sm">
                        {product?.name}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        ID: {product?.id}
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Карточка покупателя */}
              {customer && (
                <div>
                  <div className="text-gray-500 text-sm mb-2">Покупатель</div>
                  <Link
                    to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
                      brandSlug,
                      customerId: customer.id
                    })}
                    target="_blank"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Avatar 
                      circle 
                      size="md"
                      className="bg-blue-500 text-white flex-shrink-0"
                    >
                      {customer?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <div className="min-w-0">
                      <div className="font-medium text-blue-600 hover:underline text-sm">
                        {customer?.first_name} {customer?.last_name}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        ID: {customer?.id}
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </Col>
          </Row>
        </Grid>
      </Panel>

      {/* Кнопки действий */}
      <div className="flex gap-3 mb-4">
        <Button
          appearance="primary"
          color="green"
          size="lg"
          startIcon={<CheckIcon />}
          onClick={() => handleModerateReview(PRODUCT_REVIEW_STATUS_PASSED_MODERATION)}
          loading={loadingUpdateProductReview}
        >
          Опубликовать
        </Button>
        <Button
          appearance="ghost"
          size="lg"
          startIcon={<ArrowRightIcon />}
          onClick={handleNextReview}
          loading={loadingUpdateProductReview}
        >
          Пропустить
        </Button>
        <Button
          appearance="primary"
          color="red"
          size="lg"
          startIcon={<CloseIcon />}
          onClick={() => handleModerateReview(PRODUCT_REVIEW_STATUS_REJECTED)}
          loading={loadingUpdateProductReview}
        >
          Отклонить
        </Button>
        <Button
          appearance="subtle"
          size="lg"
          startIcon={<HistoryIcon />}
          onClick={handleShowHistory}
          className="ml-auto"
        >
          {state.showHistory ? 'Скрыть историю' : 'История'}
        </Button>
      </div>

      {state.showHistory && (
        <Panel className="mt-4" header="История модерирования" shaded>
          <ProductReviewsGrid filter={desicionsFilter} brandSlug={brandSlug} />
        </Panel>
      )}
    </div>
  )
}
